import Vue from 'vue';
const SEP = '::';
/** Local-date ISO helpers **/
function toLocalISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function fromLocalISO(iso) {
    if (!iso)
        return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d)
        return null;
    return new Date(y, m - 1, d);
}
function dateWithTime(date, time) {
    const match = String(time || '').trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] || 0);
    if (!Number.isFinite(hours) ||
        !Number.isFinite(minutes) ||
        !Number.isFinite(seconds) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59 ||
        seconds < 0 ||
        seconds > 59) {
        return null;
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds, 0);
}
/**
 * Supports:
 *  - "YYYY-MM-DD"
 *  - "YYYY-MM-DD::<time>" (time may be empty => "YYYY-MM-DD::")
 * Legacy fallbacks:
 *  - "YYYY-MM-DD-<anything>" (kept for backward compatibility)
 */
function splitModelValue(v) {
    if (!v)
        return { dateISO: '', time: '' };
    const direct = v.match(/^(\d{4}-\d{2}-\d{2})(?:::([\s\S]*))?$/);
    if (direct)
        return { dateISO: direct[1], time: direct[2] ?? '' };
    const legacyHyphen = v.match(/^(\d{4}-\d{2}-\d{2})-(.+)$/);
    if (legacyHyphen)
        return { dateISO: legacyHyphen[1], time: legacyHyphen[2] };
    return { dateISO: v.slice(0, 10), time: '' };
}
export default Vue.extend({
    name: 'PriceDatePicker',
    props: {
        units: { type: Array, required: true },
        selectedProductUnitIds: {
            type: Array,
            default: () => [],
        },
        value: { type: String, default: '' },
        enabled: { type: Boolean, default: true },
        minDate: { type: Date, default: () => new Date() },
        maxDate: {
            type: Date,
            default: () => new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())
        },
        timeSlots: { type: Array, required: true },
        timeSlotConfiguration: { type: Object, default: null },
        dateFieldLabel: { type: String, default: '' },
        timeFieldLabel: { type: String, default: '' },
        calendarFieldType: { type: String, default: '' },
        validDaysAfterPurchase: { default: 0 },
        validDateRangeStart: { type: String, default: '' },
        validDateRangeEnd: { type: String, default: '' },
    },
    data() {
        const { dateISO, time } = splitModelValue(this.value);
        return {
            internalDate: dateISO ? fromLocalISO(dateISO) : null,
            selectedTimeSlot: time || '',
            internalMinDate: null,
            internalMaxDate: null,
            internalMinAdvanceHours: 0,
            nowDate: new Date(),
            nowTimer: null,
            unitBlackoutSet: [],
            unitExceptionPrice: [],
            unitNormalPrice: [],
            exceptionDateUnion: new Set(),
            cts: {
                colors: {
                    orange: '#00a7e1',
                    yellow: '#00a7e1',
                    gray500: '#6B7280',
                    gray300: '#D1D5DB',
                    gray200: '#E5E7EB'
                }
            },
        };
    },
    watch: {
        value(v) {
            const { dateISO, time } = splitModelValue(v);
            const currentDateISO = this.internalDate ? toLocalISO(this.internalDate) : '';
            const sameDate = !!dateISO && currentDateISO === dateISO;
            this.internalDate = dateISO ? fromLocalISO(dateISO) : null;
            const shouldPreserveCurrentTime = this.showTimeSelect &&
                sameDate &&
                !time &&
                !!this.selectedTimeSlot;
            if (!shouldPreserveCurrentTime) {
                this.selectedTimeSlot = time || '';
            }
            this.$nextTick(() => {
                this.ensureSelectedTimeIsAvailable(false);
            });
        },
        units: {
            handler() {
                this.rebuildLookups();
                this.$nextTick(() => {
                    this.ensureSelectedTimeIsAvailable(false);
                });
            },
            deep: true,
            immediate: true
        },
        timeSlots: {
            handler() {
                this.$nextTick(() => {
                    this.ensureSelectedTimeIsAvailable(true);
                });
            },
            deep: true,
            immediate: true
        },
        timeSlotConfiguration: {
            handler() {
                this.$nextTick(() => {
                    this.ensureSelectedTimeIsAvailable(true);
                });
            },
            deep: true,
            immediate: true
        },
        selectedProductUnitIds: {
            handler() {
                this.$nextTick(() => {
                    this.ensureSelectedTimeIsAvailable(true);
                });
            },
            deep: true,
            immediate: true
        },
        selectedDateTimeSlotResolution: {
            handler(resolution) {
                this.$emit('time-requirement-change', {
                    requires: !!resolution?.requires,
                    hasAvailableSlots: !resolution?.requires ||
                        resolution.slots.length > 0,
                });
            },
            deep: true,
            immediate: true
        },
    },
    computed: {
        effectiveMinDate() {
            return this.internalMinDate || this.minDate || new Date();
        },
        effectiveMinDateStartOfDay() {
            const d = this.effectiveMinDate;
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        },
        effectiveMaxDate() {
            return this.internalMaxDate || this.maxDate;
        },
        calendarAttributes() {
            if (!this.enabled)
                return [];
            return [
                {
                    key: 'exceptions',
                    dates: (d) => this.exceptionDateUnion.has(toLocalISO(d)) && !this.isDisabledDate(d),
                    dot: true
                }
            ];
        },
        dateTitleText() {
            return this.dateFieldLabel
                ? `Select ${this.dateFieldLabel}`
                : 'Select a date';
        },
        timeTitleText() {
            return this.timeFieldLabel
                ? `Select ${this.timeFieldLabel}`
                : 'Select a time';
        },
        fixedNumberOfDaysAfterPurchaseSelected() {
            const out = `Valid for ${this.validDaysAfterPurchase} days after purchase`;
            return String(this.value).trim() === out;
        },
        fixedDateRangeSelected() {
            const out = `Valid from ${this.validDateRangeStart} to ${this.validDateRangeEnd}`;
            return String(this.value).trim() === out;
        },
        selectedDateTimeSlotResolution() {
            return this.resolveTimeSlotsForDate(this.internalDate);
        },
        requiresTimeSelectionForSelectedDate() {
            return this.selectedDateTimeSlotResolution.requires;
        },
        rawSelectableTimeSlots() {
            return this.selectedDateTimeSlotResolution.slots;
        },
        hasRealTimeSlots() {
            return this.rawSelectableTimeSlots.length > 0;
        },
        selectableTimeSlots() {
            return this.rawSelectableTimeSlots;
        },
        availableTimeSlots() {
            if (!this.internalDate) {
                return [];
            }
            return this.rawSelectableTimeSlots.filter((ts) => this.isTimeSlotAllowedForDate(this.internalDate, String(ts?.time ?? '')));
        },
        showTimeSection() {
            return (this.enabled &&
                !!this.internalDate &&
                this.requiresTimeSelectionForSelectedDate);
        },
        showTimeSelect() {
            return (this.showTimeSection &&
                this.hasRealTimeSlots);
        },
        needsTimeSelection() {
            return (this.requiresTimeSelectionForSelectedDate &&
                !!this.internalDate &&
                !this.selectedTimeSlot);
        },
        noAvailableTimeSlotsForSelectedDate() {
            return (this.showTimeSection &&
                this.availableTimeSlots.length === 0);
        },
        timeSelectPlaceholder() {
            if (!this.internalDate)
                return 'Select a date first';
            if (this.noAvailableTimeSlotsForSelectedDate)
                return 'No times available';
            return 'Select a time…';
        },
    },
    mounted() {
        this.nowTimer = setInterval(() => {
            this.nowDate = new Date();
            this.ensureSelectedTimeIsAvailable(false);
        }, 60 * 1000);
    },
    beforeDestroy() {
        if (this.nowTimer) {
            clearInterval(this.nowTimer);
        }
    },
    methods: {
        hasExplicitTimeSlotConfiguration(config) {
            if (!config) {
                return false;
            }
            return (config.requires_timeslot_selection !== null &&
                config.requires_timeslot_selection !== undefined) || (config.time_slots_json !== null &&
                config.time_slots_json !== undefined);
        },
        normalizeConfiguredTimeSlots(value) {
            if (!Array.isArray(value)) {
                return [];
            }
            return value.filter((slot) => {
                return (slot &&
                    typeof slot === 'object' &&
                    String(slot.time ?? '').trim() !== '');
            });
        },
        normalizeComparableTime(value) {
            const match = String(value ?? '')
                .trim()
                .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
            if (!match) {
                return '';
            }
            const hours = Number(match[1]);
            const minutes = Number(match[2]);
            const seconds = Number(match[3] || 0);
            if (hours < 0 ||
                hours > 23 ||
                minutes < 0 ||
                minutes > 59 ||
                seconds < 0 ||
                seconds > 59) {
                return '';
            }
            return [
                String(hours).padStart(2, '0'),
                String(minutes).padStart(2, '0'),
                String(seconds).padStart(2, '0'),
            ].join(':');
        },
        resolveTimeSlotConfigurationForUnit(productUnitId, dateISO) {
            const configuration = this.timeSlotConfiguration;
            /*
            * Old API / backward-compatible fallback.
            */
            if (!configuration) {
                const slots = this.normalizeConfiguredTimeSlots(this.timeSlots);
                return {
                    requires: slots.length > 0,
                    slots,
                };
            }
            /*
            * ---------------------------------------------------------
            * 1. Advanced Calendar
            * ---------------------------------------------------------
            */
            if (productUnitId !== null &&
                dateISO) {
                const matchingExceptions = (configuration.exceptions || [])
                    .filter((candidate) => {
                    return (candidate.unit_dates || []).some((unitDates) => {
                        return (Number(unitDates.product_unit_id) === Number(productUnitId) &&
                            Array.isArray(unitDates.dates) &&
                            unitDates.dates.includes(dateISO));
                    });
                })
                    .sort((a, b) => {
                    return (Number(b.pricing_calendar_id ?? 0) -
                        Number(a.pricing_calendar_id ?? 0));
                });
                const exception = matchingExceptions[0];
                if (exception &&
                    this.hasExplicitTimeSlotConfiguration(exception)) {
                    const requires = !!exception
                        .requires_timeslot_selection;
                    return {
                        requires,
                        slots: requires
                            ? this.normalizeConfiguredTimeSlots(exception.time_slots_json)
                            : [],
                    };
                }
            }
            /*
            * ---------------------------------------------------------
            * 2. Default Pricing Calendar
            * ---------------------------------------------------------
            */
            const defaultConfiguration = configuration.default;
            if (this.hasExplicitTimeSlotConfiguration(defaultConfiguration)) {
                const requires = !!defaultConfiguration
                    ?.requires_timeslot_selection;
                return {
                    requires,
                    slots: requires
                        ? this.normalizeConfiguredTimeSlots(defaultConfiguration
                            ?.time_slots_json)
                        : [],
                };
            }
            /*
            * ---------------------------------------------------------
            * 3. Legacy ProductVariant
            * ---------------------------------------------------------
            */
            const legacyConfiguration = configuration.legacy;
            if (this.hasExplicitTimeSlotConfiguration(legacyConfiguration)) {
                const requires = !!legacyConfiguration
                    ?.requires_timeslot_selection;
                return {
                    requires,
                    slots: requires
                        ? this.normalizeConfiguredTimeSlots(legacyConfiguration
                            ?.time_slots_json)
                        : [],
                };
            }
            /*
            * Final legacy payload fallback.
            */
            const legacySlots = this.normalizeConfiguredTimeSlots(this.timeSlots);
            return {
                requires: legacySlots.length > 0,
                slots: legacySlots,
            };
        },
        resolveTimeSlotsForDate(date) {
            const dateISO = date
                ? toLocalISO(date)
                : '';
            const units = (this.units || []);
            const selectedIds = new Set((this.selectedProductUnitIds || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) &&
                id > 0));
            /*
            * No ticket/unit has been selected yet.
            *
            * The customer is allowed to choose the date first.
            * Timeslot requirements are resolved after quantity
            * selection.
            */
            if (selectedIds.size === 0) {
                return {
                    requires: false,
                    slots: [],
                };
            }
            /*
            * Only selected ticket/unit types participate in
            * timeslot resolution.
            */
            const selectedUnits = units.filter((unit) => {
                const productUnitId = Number(unit.product_unit_id);
                return (Number.isFinite(productUnitId) &&
                    selectedIds.has(productUnitId));
            });
            if (selectedUnits.length === 0) {
                return {
                    requires: false,
                    slots: [],
                };
            }
            const resolvedByUnit = selectedUnits.map((unit) => {
                return this
                    .resolveTimeSlotConfigurationForUnit(unit.product_unit_id ?? null, dateISO);
            });
            /*
            * Units where timeslots are explicitly disabled do not
            * constrain the common selectable time.
            *
            * Backend Step 15 ignores stale time for those units.
            */
            const requiringConfigurations = resolvedByUnit.filter(configuration => configuration.requires);
            if (requiringConfigurations.length === 0) {
                return {
                    requires: false,
                    slots: [],
                };
            }
            /*
            * Because quantity/unit selection happens after the
            * date/time selection in the current UI, only expose
            * times that are valid for every unit requiring a time.
            */
            const firstSlots = requiringConfigurations[0].slots;
            const otherTimeSets = requiringConfigurations
                .slice(1)
                .map(configuration => {
                return new Set(configuration.slots
                    .map(slot => this.normalizeComparableTime(slot?.time))
                    .filter(Boolean));
            });
            const seen = new Set();
            const commonSlots = firstSlots.filter((slot) => {
                const time = this.normalizeComparableTime(slot?.time);
                if (!time || seen.has(time)) {
                    return false;
                }
                const existsForEveryUnit = otherTimeSets.every(set => set.has(time));
                if (!existsForEveryUnit) {
                    return false;
                }
                seen.add(time);
                return true;
            });
            return {
                requires: true,
                slots: commonSlots,
            };
        },
        formatTimeSlotLabel(timeSlot) {
            const time = String(timeSlot?.time ?? '').trim();
            const title = String(timeSlot?.title ?? '').trim();
            const description = String(timeSlot?.description ?? '').trim();
            const baseLabel = title || time;
            return description
                ? `${baseLabel} - ${description}`
                : baseLabel;
        },
        isTimeSlotDisabled(timeSlot) {
            if (!this.internalDate)
                return false;
            return !this.isTimeSlotAllowedForDate(this.internalDate, String(timeSlot?.time || ''));
        },
        isTimeSlotAllowedForDate(date, time) {
            if (!date)
                return true;
            const slotDateTime = dateWithTime(date, time);
            if (!slotDateTime)
                return false;
            const advanceHours = Math.max(0, Number(this.internalMinAdvanceHours) || 0);
            const cutoff = new Date(this.nowDate.getTime() + advanceHours * 60 * 60 * 1000);
            return slotDateTime.getTime() >= cutoff.getTime();
        },
        hasAvailableTimeSlotForDate(date) {
            const resolution = this.resolveTimeSlotsForDate(date);
            if (!resolution.requires) {
                return true;
            }
            if (resolution.slots.length === 0) {
                return false;
            }
            return resolution.slots.some((ts) => this.isTimeSlotAllowedForDate(date, String(ts?.time ?? '')));
        },
        ensureSelectedTimeIsAvailable(autoSelectSingleSlot = true) {
            if (!this.internalDate) {
                return;
            }
            const resolution = this.resolveTimeSlotsForDate(this.internalDate);
            /*
            * Selected date explicitly does not require a time.
            */
            if (!resolution.requires) {
                this.selectedTimeSlot = '';
                this.emitFinalSelection();
                return;
            }
            /*
            * Time is required but none are valid.
            * Do not finalize the selection.
            */
            if (resolution.slots.length === 0) {
                this.selectedTimeSlot = '';
                return;
            }
            const availableSlots = resolution.slots.filter((slot) => this.isTimeSlotAllowedForDate(this.internalDate, String(slot?.time ?? '')));
            const allowedTimes = new Set(availableSlots.map((slot) => String(slot.time)));
            if (this.selectedTimeSlot &&
                !allowedTimes.has(String(this.selectedTimeSlot))) {
                this.selectedTimeSlot = '';
            }
            if (autoSelectSingleSlot &&
                !this.selectedTimeSlot &&
                availableSlots.length === 1) {
                this.selectedTimeSlot =
                    String(availableSlots[0].time);
                this.emitFinalSelection();
            }
        },
        focusTimeSelect() {
            this.$nextTick(() => {
                const el = this.$refs.timeSelect;
                if (el && typeof el.focus === 'function')
                    el.focus();
            });
        },
        emitFinalSelection() {
            if (!this.internalDate) {
                return;
            }
            const dateISO = toLocalISO(this.internalDate);
            const resolution = this.resolveTimeSlotsForDate(this.internalDate);
            if (resolution.requires) {
                if (!this.selectedTimeSlot) {
                    return;
                }
                if (!this.isTimeSlotAllowedForDate(this.internalDate, this.selectedTimeSlot)) {
                    return;
                }
                const selectedTime = this.normalizeComparableTime(this.selectedTimeSlot);
                const exists = resolution.slots.some((slot) => this.normalizeComparableTime(slot?.time) === selectedTime);
                if (!exists) {
                    return;
                }
                const out = `${dateISO}${SEP}${this.selectedTimeSlot}`;
                this.$emit('input', out);
                this.$emit('selected', out);
                this.$emit('change', out);
                return;
            }
            /*
            * Effective calendar has no timeslot requirement.
            */
            this.selectedTimeSlot = '';
            this.$emit('input', dateISO);
            this.$emit('selected', dateISO);
            this.$emit('change', dateISO);
        },
        onDayCellClick(d) {
            if (!this.enabled)
                return;
            if (this.isDisabledDate(d))
                return;
            this.internalDate = d;
            const dateISO = toLocalISO(d);
            this.$emit('date-selected', dateISO);
            if (this.requiresTimeSelectionForSelectedDate) {
                const allowedTimes = new Set(this.availableTimeSlots.map((ts) => String(ts.time)));
                if (this.selectedTimeSlot && !allowedTimes.has(String(this.selectedTimeSlot))) {
                    this.selectedTimeSlot = '';
                }
                if (this.availableTimeSlots.length === 0) {
                    this.focusTimeSelect();
                    return;
                }
                if (!this.selectedTimeSlot && this.availableTimeSlots.length === 1) {
                    this.selectedTimeSlot = String(this.availableTimeSlots[0].time);
                    this.emitFinalSelection();
                    return;
                }
                if (!this.selectedTimeSlot) {
                    this.focusTimeSelect();
                    return;
                }
            }
            else {
                this.selectedTimeSlot = '';
            }
            this.emitFinalSelection();
        },
        onTimeSlotChange(event) {
            const el = event.target;
            this.selectedTimeSlot = String(el.value || '');
            if (!this.selectedTimeSlot)
                return;
            if (!this.isTimeSlotAllowedForDate(this.internalDate, this.selectedTimeSlot)) {
                this.selectedTimeSlot = '';
                return;
            }
            this.$nextTick(() => {
                this.emitFinalSelection();
            });
        },
        onFixedNumberOfDaysAfterPurchaseClick() {
            const out = `Valid for ${this.validDaysAfterPurchase} days after purchase`;
            this.$emit('input', out);
            this.$emit('selected', out);
            this.$emit('change', out);
        },
        onFixedDateRangeClick() {
            const out = `Valid from ${this.validDateRangeStart} to ${this.validDateRangeEnd}`;
            this.$emit('input', out);
            this.$emit('selected', out);
            this.$emit('change', out);
        },
        isSelectedDateRange(start, end) {
            if (!start || !end)
                return false;
            return this.isSelected(start) && this.isSelected(end);
        },
        isToday(d) {
            const now = new Date();
            return d.getFullYear() === now.getFullYear() &&
                d.getMonth() === now.getMonth() &&
                d.getDate() === now.getDate();
        },
        isDisabledDate(d) {
            const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const minDate = this.effectiveMinDateStartOfDay;
            if (dayStart < minDate)
                return true;
            const maxDate = this.effectiveMaxDate;
            if (maxDate) {
                const maxDayStart = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
                if (dayStart > maxDayStart)
                    return true;
            }
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            if (dayStart < today)
                return true;
            if (!this.enabled || !this.units?.length)
                return false;
            if (!this.hasAvailableTimeSlotForDate(d)) {
                return true;
            }
            const key = toLocalISO(d);
            return this.unitBlackoutSet.length > 0
                ? this.unitBlackoutSet.every(set => set.has(key))
                : false;
        },
        isException(d) {
            if (!this.enabled)
                return false;
            return this.exceptionDateUnion.has(toLocalISO(d));
        },
        isSelected(d) {
            if (!this.internalDate)
                return false;
            return toLocalISO(this.internalDate) === toLocalISO(d);
        },
        fromPrice(d) {
            const key = toLocalISO(d);
            const prices = [];
            for (let i = 0; i < this.units.length; i++) {
                if (this.unitBlackoutSet[i]?.has(key))
                    continue;
                const ex = this.unitExceptionPrice[i]?.get(key);
                if (ex != null) {
                    if (Number.isFinite(ex) && ex > 0)
                        prices.push(ex);
                    continue;
                }
                const norm = this.unitNormalPrice[i];
                if (norm != null) {
                    if (Number.isFinite(norm) && norm > 0)
                        prices.push(norm);
                    continue;
                }
                const base = Number(this.units[i].price / 100);
                if (Number.isFinite(base) && base > 0) {
                    prices.push(base);
                }
            }
            return prices.length ? Math.min(...prices) : NaN;
        },
        rebuildLookups() {
            this.unitBlackoutSet = [];
            this.unitExceptionPrice = [];
            this.unitNormalPrice = [];
            this.exceptionDateUnion.clear();
            let nextMinDate = null;
            let nextMaxDate = null;
            let nextMinAdvanceHours = 0;
            for (const u of (this.units || [])) {
                const pd = u.pricing_details;
                const pdStartDate = pd?.start_date ? fromLocalISO(pd.start_date) : null;
                const pdEndDate = pd?.end_date ? fromLocalISO(pd.end_date) : null;
                if (pdStartDate && (!nextMinDate || pdStartDate > nextMinDate)) {
                    nextMinDate = pdStartDate;
                }
                if (pdEndDate && (!nextMaxDate || pdEndDate < nextMaxDate)) {
                    nextMaxDate = pdEndDate;
                }
                const advanceHours = Number(pd?.min_advance_hours);
                if (Number.isFinite(advanceHours)) {
                    nextMinAdvanceHours = Math.max(nextMinAdvanceHours, advanceHours);
                }
                const bo = new Set();
                (pd?.blackouts || []).forEach((g) => g.days?.forEach((d) => bo.add(d.date)));
                this.unitBlackoutSet.push(bo);
                const exMap = new Map();
                const sortedExceptions = [...(pd?.exceptions || [])]
                    .sort((a, b) => {
                    return (Number(b.id ?? 0) -
                        Number(a.id ?? 0));
                });
                sortedExceptions.forEach((g) => {
                    g.days?.forEach((day) => {
                        const pid = u.product_unit_id;
                        /*
                        * Newest Advanced Calendar has
                        * already populated this date.
                        *
                        * Never allow an older calendar
                        * to overwrite it.
                        */
                        if (exMap.has(day.date)) {
                            return;
                        }
                        if (pid != null) {
                            const hit = day.units?.find((x) => Number(x.product_unit_id) === Number(pid));
                            if (hit) {
                                exMap.set(day.date, hit.price / 100);
                                this.exceptionDateUnion.add(day.date);
                            }
                        }
                        else if (day.units &&
                            day.units.length === 1) {
                            exMap.set(day.date, day.units[0].price / 100);
                            this.exceptionDateUnion.add(day.date);
                        }
                    });
                });
                this.unitExceptionPrice.push(exMap);
                let normal = null;
                if (pd?.normal?.length) {
                    const pid = u.product_unit_id;
                    if (pid != null) {
                        const hit = pd.normal.find((n) => n.product_unit_id === pid);
                        if (hit)
                            normal = hit.price / 100;
                    }
                    else if (pd.normal.length === 1) {
                        normal = pd.normal[0].price / 100;
                    }
                }
                if (normal == null && Number.isFinite(Number(u.price / 100))) {
                    normal = Number(u.price / 100);
                }
                this.unitNormalPrice.push(normal);
            }
            this.internalMinDate = nextMinDate;
            this.internalMaxDate = nextMaxDate;
            this.internalMinAdvanceHours = nextMinAdvanceHours;
        },
        toMoney(dollars) {
            if (dollars == null || Number.isNaN(dollars))
                return '';
            return Number(dollars).toFixed(2);
        }
    }
});
